import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// asset_types table; LibraryMapping.ToDto(AssetType) in lureo-backend Data/Api/V1/Library/LibraryDtos.cs
export type AssetTypeDto = components['schemas']['AssetTypeDto'];
export type CreateAssetTypeRequest = components['schemas']['CreateAssetTypeRequest'];

const spec = {
  uniqueId: 'asset_type_id',
  createdAt: 'created_at',
  userUniqueId: 'user_id',
  orgInstanceUniqueId: 'org_instance_id',
  displayName: 'asset_type_display_name',
  sourceName: 'asset_type_source_name',
  category: 'asset_type_category',
  tags: 'asset_type_tags',
  // The entity holds details as an object; v1 returns an object ({} when the stored value is not one)
  details: { legacy: 'asset_type_details', as: 'json' },
  imgUrl: 'asset_type_img_url',
  // v1 unwraps a JSON string holding JSON once and returns null for text that is not JSON
  animations: { legacy: 'asset_type_animations', as: 'jsonText' },
  sourceUrl: 'asset_type_source_url',
  optimizedUrl: 'asset_type_optimized_url',
  position: 'asset_type_position',
  rotation: 'asset_type_rotation',
  scale: 'asset_type_scale',
  isActive: 'asset_type_is_active',
  beginTime: 'asset_type_begin_time',
  endTime: 'asset_type_end_time',
  displayOn: 'asset_type_display_on',
  availability: 'asset_type_availability',
  longitude: 'asset_type_longitude',
  latitude: 'asset_type_latitude',
  altitude: 'asset_type_altitude',
  status: 'asset_type_status',
  hasPackedAlpha: 'asset_type_has_packed_alpha',
  // The converter's measurements, read only. asset_type_clips already names the type's clip types
  rig: 'asset_type_rig_profile',
  clips: 'asset_type_clip_profiles',
  // What the user changed on the model; written only through modelEdits, as a change converts the type again
  edits: 'asset_type_edits',
} as const satisfies FieldSpec<AssetTypeDto>;

// A write skips them, so a row copied from a read (a form fork) still creates
const CONVERTER_OWNED = ['asset_type_rig_profile', 'asset_type_clip_profiles', 'asset_type_edits'] as const;

const map = fieldMap<AssetTypeDto, typeof spec>('Asset type', spec);

export type LegacyAssetType = LegacyRow<typeof spec, AssetTypeDto>;

/** updateAssetType accepts these. */
export const ASSET_TYPE_PATCH_FIELDS = [
  'displayName', 'sourceName', 'category', 'tags', 'details', 'imgUrl', 'animations', 'sourceUrl', 'optimizedUrl',
  'position', 'rotation', 'scale', 'isActive', 'beginTime', 'endTime', 'displayOn', 'availability', 'longitude',
  'latitude', 'altitude', 'status', 'hasPackedAlpha',
] as const;
const CREATE_FIELDS = ['uniqueId', 'orgInstanceUniqueId', ...ASSET_TYPE_PATCH_FIELDS] as const;

export const assetTypeToLegacy = (dto: AssetTypeDto): LegacyAssetType => map.toLegacy(dto);

/** createAssetType body from a legacy insert row; asset_type_id is required. The owner is the caller. */
export function assetTypeCreateBody(row: Partial<LegacyAssetType>): CreateAssetTypeRequest {
  const body = map.toBody(row, CREATE_FIELDS, ['user_id', 'created_at', ...CONVERTER_OWNED]);
  return { ...body, uniqueId: requireId('Asset type', 'asset_type_id', body.uniqueId) };
}

/** updateAssetType body from a legacy partial update. */
export const assetTypePatchBody = (changes: Partial<LegacyAssetType>) =>
  map.toBody(changes, ASSET_TYPE_PATCH_FIELDS, CONVERTER_OWNED);
